import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useEvents } from '@/hooks/useEvents';
import { ProgressIndicator } from '@/components/registration/ProgressIndicator';
import { RegistrationWizard } from '@/components/registration/RegistrationWizard';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from "@/hooks/use-mobile";
import { usePlatform } from "@/hooks/usePlatform";
import { useLanguage } from '@/contexts/LanguageContext';

const RegistrationPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { events, loading } = useEvents();
  const isMobile = useIsMobile();
  const { isNative, isIOS, isAndroid } = usePlatform();
  const { t } = useLanguage();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({});

  // Prioritize event from navigation state, then database, then localStorage cache
  let event = location.state?.event || events.find(e => e.id === eventId);

  // Try localStorage cache before using hardcoded fallback (handles Stripe redirect losing state)
  if (!event && eventId) {
    try {
      const cached = localStorage.getItem('pendingEventData');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.id === eventId) {
          event = parsed;
        }
      }
    } catch {}
  }

  // Save event to localStorage whenever we have it (so Stripe redirects can recover it)
  useEffect(() => {
    if (event && event.id === eventId) {
      localStorage.setItem('pendingEventData', JSON.stringify(event));
    }
  }, [event?.id, eventId]);
  
  // Get step from URL params or default to 1
  useEffect(() => {
    const step = searchParams.get('step');
    if (step) {
      setCurrentStep(parseInt(step));
    } else {
      // If no step in URL, default to step 1 and update URL
      setCurrentStep(1);
      setSearchParams({ step: '1' });
    }
  }, [searchParams, setSearchParams]);

  // Update URL when step changes
  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    setSearchParams({ step: step.toString() });
  };

  const handleBack = () => {
    // Always use the original event from navigation state to preserve complete data
    const originalEvent = location.state?.event;
    
    // Navigate back with event information to show the detailed view
    navigate('/?tab=events', { 
      state: { 
        showEventDetail: true, 
        eventId: eventId,
        event: originalEvent || event // Only fallback if no original event
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className={isMobile ? 'text-sm' : ''}>{t('registration.page.loading')}</span>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-4`}>
            {t('registration.page.notFound')}
          </h2>
          <Button 
            onClick={() => navigate('/')}
            className={isMobile ? 'w-full' : ''}
          >
            {t('registration.page.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - Mobile optimized with safe area */}
      <div 
        className="bg-white shadow-sm border-b sticky top-0 z-50"
        style={{
          paddingTop: isNative && (isIOS || isAndroid) ? '44px' : '0px'
        }}
      >
        <div className={`${isMobile ? 'max-w-full' : 'max-w-4xl'} mx-auto px-4 py-4`}>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className={`flex items-center gap-2 ${isMobile ? 'p-2' : ''}`}
            >
              <ArrowLeft className="h-4 w-4" />
              {!isMobile && t('registration.page.back')}
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900 truncate`}>
                {t('registration.page.registrationTo')} {event.title}
              </h1>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
                {event.sport} • {event.event_type === 'league' ? t('registration.page.league') : t('registration.page.tournament')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Indicator - Mobile optimized */}
      <div className={`${isMobile ? 'max-w-full px-3 py-4' : 'max-w-4xl mx-auto px-4 py-6'}`}>
        <ProgressIndicator currentStep={currentStep} />
      </div>

      {/* Main Content - Mobile optimized */}
      <div className={`${isMobile ? 'max-w-full px-3 pb-6' : 'max-w-4xl mx-auto px-4 pb-8'}`}>
        <Card className={isMobile ? 'shadow-sm' : ''}>
          <CardContent className={isMobile ? 'p-4' : 'p-6'}>
            <RegistrationWizard
              event={event}
              currentStep={currentStep}
              onStepChange={handleStepChange}
              registrationData={registrationData}
              onDataChange={setRegistrationData}
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Safe area spacer for bottom in native apps */}
      {isNative && (isIOS || isAndroid) && <div className="h-[20px] bg-gray-50" />}
    </div>
  );
};

export default RegistrationPage;
